import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faSort,
  faSortDown,
  faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import type { Column, SortState } from './WbsTable';

/**
 * Gets the filtered visible columns based on visibility state
 */
export function getVisibleColumns<T>(
  columns: Column<T>[],
  visibleColumns: Set<keyof T>,
): Column<T>[] {
  return columns.filter((col) => visibleColumns.has(col.key));
}

/**
 * Handles sort state transitions when a column header is clicked
 */
export function getNextSortState<T>(
  currentSortState: SortState<T>,
  clickedColumn: keyof T,
): SortState<T> {
  if (currentSortState.column === clickedColumn) {
    // Same column clicked - cycle through asc -> desc -> null
    if (currentSortState.direction === 'asc') {
      return { column: clickedColumn, direction: 'desc' };
    }
    if (currentSortState.direction === 'desc') {
      return { column: null, direction: null };
    }
    return { column: clickedColumn, direction: 'asc' };
  }
  // Different column clicked - start with asc
  return { column: clickedColumn, direction: 'asc' };
}

/**
 * Toggles column visibility in the provided set
 */
export function toggleColumnVisibility<T>(
  visibleColumns: Set<keyof T>,
  columnKey: keyof T,
): Set<keyof T> {
  const newSet = new Set(visibleColumns);
  if (newSet.has(columnKey)) {
    newSet.delete(columnKey);
  } else {
    newSet.add(columnKey);
  }
  return newSet;
}

/**
 * Resets column visibility to default (excludes specified columns)
 */
export function getDefaultVisibleColumns<T>(
  columns: Column<T>[],
  hiddenColumns: string[] = [],
): Set<keyof T> {
  return new Set(
    columns
      .map((col) => col.key)
      .filter((key) => !hiddenColumns.includes(String(key))),
  );
}

/**
 * Gets the appropriate sort icon based on current sort state
 */
export function getSortIcon<T>(
  sortState: SortState<T>,
  column: keyof T,
): IconDefinition {
  if (sortState.column === column) {
    if (sortState.direction === 'asc') return faSortUp;
    if (sortState.direction === 'desc') return faSortDown;
  }
  return faSort;
}

/**
 * Sorts data based on sort state and column configurations
 */
export function sortData<T extends Record<string, any>>(
  data: T[],
  sortState: SortState<T>,
  visibleColumns: Column<T>[],
): T[] {
  if (!sortState.column || !sortState.direction) {
    return data;
  }

  const column = visibleColumns.find((col) => col.key === sortState.column);

  return [...data].sort((a, b) => {
    // Use custom sort function if provided
    if (column?.sortFn) {
      const result = column.sortFn(a, b);
      return sortState.direction === 'asc' ? result : -result;
    }

    // Default sorting logic
    let valueA: any = a[sortState.column as keyof T];
    let valueB: any = b[sortState.column as keyof T];

    // Handle different data types
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
    }

    if (typeof valueA === 'string' && !Number.isNaN(Number(valueA))) {
      valueA = Number(valueA);
      valueB = Number(valueB);
    }

    if (valueA < valueB) {
      return sortState.direction === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return sortState.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
}
